/* Informational presentation only. Native inscription, cart and localization keep their owners. */
(() => {
  'use strict';
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const seen = new WeakSet();
  const pending = new Set();
  const finishReveal = node => {
    node.removeAttribute('data-tf-pending');
    pending.delete(node);
    seen.add(node);
    observer?.unobserve(node);
  };
  const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
    entries.forEach(entry => { if (entry.isIntersecting) finishReveal(entry.target); });
  }, { threshold: 0, rootMargin: '0px 0px -64px 0px' }) : null;
  const revealParents = node => {
    while (node instanceof Element) {
      if (node.hasAttribute('data-tf-reveal')) finishReveal(node);
      node = node.parentElement;
    }
  };
  const revealFragment = () => {
    if (!window.location.hash) return;
    try { revealParents(document.getElementById(decodeURIComponent(window.location.hash.slice(1)))); } catch {}
  };
  const initReveals = scope => {
    if (reduced.matches || !observer) return;
    document.documentElement.classList.add('tf-presentation-ready');
    const nodes = Array.from(scope.querySelectorAll('[data-tf-reveal]'));
    if (scope instanceof Element && scope.matches('[data-tf-reveal]')) nodes.unshift(scope);
    nodes.forEach(node => {
      if (seen.has(node) || pending.has(node)) return;
      // First viewport, current keyboard targets and nested groups remain immediate.
      if (node.getBoundingClientRect().top < window.innerHeight || node.contains(document.activeElement) || node.parentElement?.closest('[data-tf-reveal]')) {
        finishReveal(node); return;
      }
      node.setAttribute('data-tf-pending', '');
      Array.from(node.children).forEach((child, index) => {
        child.style.setProperty('--tf-reveal-delay', `${Math.min(index, 2) * 80}ms`);
      });
      pending.add(node);
      observer.observe(node);
    });
    revealFragment();
  };
  document.addEventListener('focusin', event => revealParents(event.target));
  window.addEventListener('hashchange', revealFragment);

  const faqControllers = new WeakMap();
  const active = new Set();
  const initFaqs = scope => scope.querySelectorAll('.tf-informational-faq details').forEach(details => {
    if (faqControllers.has(details)) return;
    const summary = details.querySelector(':scope > summary');
    const body = details.querySelector(':scope > .tf-faq-answer');
    if (!summary || !body || typeof details.animate !== 'function') return;
    const state = { target: details.open, animation: null, started: 0, end: 0, resize: null };
    const cancelAnimation = () => {
      if (!state.animation) return;
      state.animation.onfinish = null;
      state.animation.cancel();
      state.animation = null;
    };
    const settle = () => {
      cancelAnimation();
      if (!state.target && body.contains(document.activeElement)) summary.focus({ preventScroll: true });
      details.open = state.target;
      details.removeAttribute('data-tf-faq-animating');
      details.removeAttribute('data-tf-faq-target');
      active.delete(state);
    };
    const animateTo = (height, duration = 320) => {
      const from = details.getBoundingClientRect().height;
      cancelAnimation();
      if (duration <= 0 || Math.abs(height - from) < 1) { settle(); return; }
      state.end = height;
      state.started = performance.now();
      state.duration = duration;
      details.setAttribute('data-tf-faq-animating', '');
      let animation;
      try {
        animation = details.animate([{ height: `${from}px` }, { height: `${height}px` }], {
          duration, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards'
        });
      } catch { settle(); return; }
      state.animation = animation;
      active.add(state);
      animation.onfinish = () => { if (state.animation === animation) settle(); };
    };
    const naturalHeight = () => summary.getBoundingClientRect().height + body.scrollHeight +
      parseFloat(getComputedStyle(details).borderTopWidth) + parseFloat(getComputedStyle(details).borderBottomWidth);
    const onClick = event => {
      if (reduced.matches) return;
      event.preventDefault();
      const from = details.getBoundingClientRect().height;
      state.target = !state.target;
      if (!state.target && body.contains(document.activeElement)) summary.focus({ preventScroll: true });
      cancelAnimation();
      details.open = true;
      details.setAttribute('data-tf-faq-target', String(state.target));
      const target = state.target ? naturalHeight() : summary.getBoundingClientRect().height +
        parseFloat(getComputedStyle(details).borderTopWidth) + parseFloat(getComputedStyle(details).borderBottomWidth);
      // Pin the displayed height before animating a formerly closed native details element.
      let animation;
      try {
        animation = details.animate([{ height: `${from}px` }, { height: `${target}px` }], {
          duration: 320, easing: 'cubic-bezier(.22,1,.36,1)', fill: 'forwards'
        });
      } catch { settle(); return; }
      details.setAttribute('data-tf-faq-animating', '');
      state.animation = animation; state.end = target; state.started = performance.now(); state.duration = 320;
      active.add(state);
      animation.onfinish = () => { if (state.animation === animation) settle(); };
    };
    const onToggle = () => {
      if (!state.animation) state.target = details.open;
    };
    summary.addEventListener('click', onClick);
    details.addEventListener('toggle', onToggle);
    if ('ResizeObserver' in window) {
      state.resize = new ResizeObserver(() => {
        if (!state.animation || !state.target) return;
        const target = naturalHeight();
        if (Math.abs(target - state.end) < 1) return;
        const remaining = Math.max(0, state.duration - (performance.now() - state.started));
        animateTo(target, remaining);
      });
      state.resize.observe(body);
      state.resize.observe(summary);
    }
    state.settle = settle;
    state.destroy = () => {
      settle();
      state.resize?.disconnect();
      summary.removeEventListener('click', onClick);
      details.removeEventListener('toggle', onToggle);
      faqControllers.delete(details);
    };
    faqControllers.set(details, state);
  });
  // One photographic body; native scrolling, text and static media remain independent.
  const stories = new Map();
  const rendererLoads = new Map();
  const loadTurn = source => {
    if (window.TFProductTurn) return Promise.resolve(window.TFProductTurn);
    if (!rendererLoads.has(source)) rendererLoads.set(source,new Promise((resolve,reject) => {
      const script=document.createElement('script');
      let timer;
      const finish=error => {
        clearTimeout(timer); script.onload=null; script.onerror=null;
        if (error) { script.remove(); rendererLoads.delete(source); reject(error); }
        else resolve(window.TFProductTurn);
      };
      script.src=source; script.async=true;
      script.onload=()=>finish(window.TFProductTurn?null:new Error('Product viewer unavailable'));
      script.onerror=()=>finish(new Error('Product viewer unavailable'));
      timer=setTimeout(()=>finish(new Error('Product viewer timeout')),12000);
      document.head.appendChild(script);
    }));
    return rendererLoads.get(source);
  };
  const clamp = value => Math.max(0,Math.min(1,value));
  // Copy has a finite dissolve. The product itself tracks scroll directly.
  const storyEase = progress => progress*progress*(3-2*progress);
  const storyPose = (distance,timing) => {
    const {entry,travel,exit}=timing;
    return 180*clamp((distance+entry)/(entry+travel+exit));
  };
  const initStories = scope => {
    if (typeof window.TFProductStoryLayout !== 'function') return;
    const nodes=Array.from(scope.querySelectorAll('[data-tf-product-story]'));
    if (scope.matches?.('[data-tf-product-story]')) nodes.unshift(scope);
    nodes.forEach(node => {
      if (stories.has(node)) return;
      const pin=node.querySelector('.tf-product-story__pin');
      const band=node.querySelector('.tf-product-story__band');
      const copy=node.querySelector('.tf-product-story__copy-group');
      const canvas=node.querySelector('canvas');
      const controls=node.querySelector('.tf-product-story__controls');
      const action=node.querySelector('[data-tf-story-action]');
      const contained=node.querySelector('[data-tf-story-contained-action]');
      const flowing=node.querySelector('[data-tf-story-flow-action]');
      const figures=Array.from(node.querySelectorAll('[data-tf-story-face]'));
      figures.forEach(figure=>{
        const image=figure.querySelector('img');
        const usePng=()=>figure.querySelector('source')?.remove();
        image?.addEventListener('error',usePng,{once:true});
        if(image?.complete && !image.naturalWidth)usePng();
      });
      const buttons=Array.from(node.querySelectorAll('[data-tf-story-select]'));
      if (!pin || !band || !copy || !canvas || !controls || !action || !contained || !flowing || figures.length!==2) return;
      node.tfStoryLayoutCleanup?.();
      const state={frame:0,visible:false,near:false,moving:false,failed:false,loading:false,view:null,generation:0,width:window.innerWidth,height:window.innerHeight,observer:null,resize:null,abort:null,suspended:false,destroyed:false,blend:0,phaseTarget:0,phaseFrom:0,phaseStarted:0,phaseDuration:520,phaseActive:false,selected:'front',snap:true,timing:{entry:1,travel:1,exit:1}};
      const paintPhase = blend => {
        state.blend=blend;
        node.style.setProperty('--tf-story-front-opacity',String(1-blend));
        node.style.setProperty('--tf-story-back-opacity',String(blend));
      };
      const advancePhase = now => {
        if (!state.phaseActive) return;
        const progress=clamp((now-state.phaseStarted)/state.phaseDuration);
        paintPhase(state.phaseFrom+(state.phaseTarget-state.phaseFrom)*storyEase(progress));
        if (progress>=1) {state.phaseActive=false;paintPhase(state.phaseTarget);}
      };
      const targetPhase = (target,instant=false) => {
        const now=performance.now();
        advancePhase(now);
        if (instant) {state.phaseTarget=target;state.phaseActive=false;paintPhase(target);return;}
        if (state.phaseTarget===target) return;
        state.phaseFrom=state.blend;state.phaseTarget=target;state.phaseStarted=now;
        state.phaseDuration=Math.max(120,520*Math.abs(target-state.blend));
        state.phaseActive=state.blend!==target;
      };
      const selectFaces = face => {
        state.selected=face;
        figures.forEach(figure=>{figure.hidden=figure.dataset.tfStoryFace!==face;});
        buttons.forEach(button=>button.setAttribute('aria-pressed',String(button.dataset.tfStorySelect===face)));
      };
      const select = face => {selectFaces(face);targetPhase(face==='front'?0:1,true);};
      const showAngle = (angle,instant=false) => {
        state.view?.set(angle);
        // A late download must not swap a face-on poster for a different pose.
        // The scrub itself keeps its original timing; hand over only at the
        // matching front/reverse endpoint if loading finished mid-turn.
        if (state.view && (angle===0 || angle===180)) node.classList.add('tf-product-story--ready');
        state.selected=angle<90?'front':'back';
        if (node.classList.contains('tf-product-story--ready')) figures.forEach(figure=>{figure.hidden=false;});
        else selectFaces(angle<90?'front':'back');
        if (instant) targetPhase(angle>=90?1:0,true);
        else if (angle>=92) targetPhase(1);
        else if (angle<=88) targetPhase(0);
      };
      const measureTiming = () => {
        const bandRect=band.getBoundingClientRect(),mediaRect=canvas.getBoundingClientRect();
        const header=parseFloat(node.style.getPropertyValue('--tf-story-header'))||100;
        const offset=mediaRect.top-bandRect.top;
        const usable=Math.max(0,state.height-header);
        // Local offsets survive the sticky position. An absolute sticky stage
        // position would freeze halfway through the sequence.
        const phoneHomeDelay=state.width<=720 && !node.classList.contains('tf-product-story--media-left') ? 20 : 0;
        const entry=Math.max(1,usable-offset-mediaRect.height/2-phoneHomeDelay);
        const travel=Math.max(1,pin.getBoundingClientRect().height-bandRect.height);
        // At the end of the pin, 80% of this one linear turn is complete.
        // The final 20% continues during normal flow, without another spacer,
        // easing segment or visibility cap changing the angular rate.
        const exit=(entry+travel)*.2/.8;
        state.timing={entry,travel,exit};
        node.style.setProperty('--tf-story-entry',entry+'px');
        node.style.setProperty('--tf-story-exit',exit+'px');
      };
      const currentAngle = () => {
        const header=parseFloat(node.style.getPropertyValue('--tf-story-header'))||100;
        return storyPose(header-pin.getBoundingClientRect().top,state.timing);
      };
      const update = () => {
        state.frame=0;
        if (state.suspended || !state.moving || !node.isConnected) return;
        // The flowing action is outside this pin: it cannot lengthen the scrub.
        const angle=currentAngle();
        try {showAngle(angle,state.snap || !state.visible);} catch {fail();return;}
        state.snap=false;
        advancePhase(performance.now());
        if (state.phaseActive) schedule();
      };
      const schedule = () => {
        if (!state.frame && !state.suspended && state.visible && state.moving) state.frame=requestAnimationFrame(update);
      };
      const release = () => {
        state.generation++;state.abort?.abort();state.abort=null;
        state.view?.destroy();state.view=null;state.loading=false;
        selectFaces(state.selected);
        node.classList.remove('tf-product-story--ready');
      };
      const fail = () => {
        if (!node.isConnected || state.failed || state.destroyed) return;
        state.failed=true;release();configure();
      };
      const start = async () => {
        if (!state.moving || state.view || state.loading || state.failed || !state.near || state.suspended || state.destroyed) return;
        state.loading=true;
        const generation=++state.generation;
        const abort=new AbortController();state.abort=abort;
        try {
          const engine=await loadTurn(node.dataset.tfTurnScript);
          if (generation!==state.generation || !node.isConnected || !state.moving || state.suspended) return;
          const view=await engine.create(canvas,node.dataset.tfTurnTexture,fail,abort.signal);
          if (generation!==state.generation || !node.isConnected || !state.moving || state.failed || state.suspended) {view.destroy();return;}
          state.view=view;
          measureTiming();state.snap=true;
          cancelAnimationFrame(state.frame);state.frame=0;update();
          if (generation!==state.generation || state.view!==view || state.failed || !state.moving) return;
          state.loading=false;schedule();
        } catch {if (generation===state.generation && node.isConnected) fail();}
      };
      const moveAction = compact => {
        const host=compact?flowing:contained;
        if (compact) flowing.hidden=false;
        if (action.parentElement!==host) {
          const focused=action.contains(document.activeElement)?document.activeElement:null;
          host.appendChild(action);
          // Moving the same node keeps its destination and listeners. Restore a
          // focused link if the browser lost focus when its parent changed.
          if (focused) {
            focused.focus({preventScroll:true});
            const rect=focused.getBoundingClientRect();
            if (rect.top<0 || rect.bottom>window.innerHeight) focused.scrollIntoView?.({block:'nearest',behavior:'instant'});
          }
        }
        flowing.hidden=!compact;
      };
      const configure = () => {
        if (state.destroyed || state.suspended || !node.isConnected) return;
        const width=window.innerWidth;
        // Keep phone browser-chrome height changes from repeatedly moving the
        // focused action or changing the pin while scrolling. A width/orientation
        // change, desktop resize or font/header measurement still refits it.
        const height=width===state.width && width<=960?state.height:window.innerHeight;
        const {moving,compact}=window.TFProductStoryLayout(node,{height,manual:state.failed});
        if (!moving) {cancelAnimationFrame(state.frame);state.frame=0;release();}
        state.moving=moving;state.width=width;state.height=height;
        moveAction(compact);
        if (moving && controls.contains(document.activeElement)) action.querySelector('a')?.focus();
        controls.hidden=moving;
        if (!moving) select(state.selected);
        try {state.view?.resize();if (moving) measureTiming();} catch {fail();return;}
        start();schedule();
      };
      const onResize = () => {
        const desktopHeightChanged=window.innerWidth>960 && window.innerHeight!==state.height;
        if (window.innerWidth!==state.width || desktopHeightChanged) configure();
        else {try {state.view?.resize();if (state.moving) measureTiming();schedule();} catch {fail();}}
      };
      const onOrientation = () => {state.width=0;configure();};
      const onSelect = event => {if (!state.moving) select(event.currentTarget.dataset.tfStorySelect);};
      buttons.forEach(button=>button.addEventListener('click',onSelect));
      window.addEventListener('resize',onResize,{passive:true});
      window.addEventListener('orientationchange',onOrientation);
      reduced.addEventListener('change',configure);
      if ('IntersectionObserver' in window) {
        state.observer=new IntersectionObserver(entries=>{
          state.near=entries.some(entry=>entry.isIntersecting);
          const rect=band.getBoundingClientRect();
          const wasVisible=state.visible;
          state.visible=state.near && rect.bottom>0 && rect.top<window.innerHeight;
          if (state.visible && !wasVisible) state.snap=true;
          if (state.near) start();
          if (state.visible) schedule();
          else {cancelAnimationFrame(state.frame);state.frame=0;targetPhase(state.moving?(currentAngle()>=90?1:0):state.phaseTarget,true);}
        },{rootMargin:'900px 0px'});
        state.observer.observe(node);
      } else {state.near=true;state.visible=true;}
      const onScroll = () => {
        const rect=band.getBoundingClientRect();
        const wasVisible=state.visible;
        state.visible=rect.bottom>0 && rect.top<window.innerHeight;
        if (state.visible && !wasVisible) state.snap=true;
        if (state.visible) schedule();
        else {cancelAnimationFrame(state.frame);state.frame=0;targetPhase(state.moving?(currentAngle()>=90?1:0):state.phaseTarget,true);}
      };
      window.addEventListener('scroll',onScroll,{passive:true});
      if ('ResizeObserver' in window) {
        state.resize=new ResizeObserver(configure);
        state.resize.observe(copy);state.resize.observe(action);
        const header=document.querySelector('.tf-header');
        if (header) state.resize.observe(header);
      }
      document.fonts?.ready.then(()=>{if (!state.destroyed && !state.suspended) configure();});
      state.suspend=()=>{
        state.suspended=true;cancelAnimationFrame(state.frame);state.frame=0;
        targetPhase(state.phaseTarget,true);release();
      };
      state.resume=()=>{state.suspended=false;state.width=0;configure();};
      state.destroy=()=>{
        state.suspend();state.destroyed=true;state.observer?.disconnect();state.resize?.disconnect();
        buttons.forEach(button=>button.removeEventListener('click',onSelect));
        window.removeEventListener('scroll',onScroll);window.removeEventListener('resize',onResize);
        window.removeEventListener('orientationchange',onOrientation);reduced.removeEventListener('change',configure);
        stories.delete(node);
      };
      stories.set(node,state);configure();
    });
  };

  // Load the fixed agency orbit only as its own footer position approaches.
  // The canonical art/script stays untouched; its static link remains usable.
  let badgeTarget = null, badgeObserver = null, badgeStarted = false;
  const loadBadge = target => {
    if (badgeStarted || !target.isConnected) return;
    const source = target.dataset.tfBadgeScript;
    if (!source) return;
    badgeStarted = true;
    badgeObserver?.disconnect();
    badgeObserver = null;
    const script = document.createElement('script');
    script.src = source;
    script.defer = true;
    script.dataset.tfBadgeOrbit = '';
    document.head.appendChild(script);
  };
  const initBadge = scope => {
    if (badgeStarted) return;
    const target = scope.querySelector('[data-tf-badge-script]');
    if (!target || target === badgeTarget) return;
    badgeObserver?.disconnect();
    badgeTarget = target;
    if (!('IntersectionObserver' in window)) { loadBadge(target); return; }
    badgeObserver = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target === badgeTarget) loadBadge(entry.target);
      });
    }, { rootMargin: '240px', threshold: 0 });
    badgeObserver.observe(target);
  };
  const init = scope => {
    try { initReveals(scope); }
    catch {
      Array.from(pending).forEach(finishReveal);
      document.documentElement.classList.remove('tf-presentation-ready');
    }
    initFaqs(scope);
    initStories(scope);
    initBadge(scope);
  };
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    if (badgeTarget && event.target.contains(badgeTarget)) { badgeObserver?.disconnect(); badgeObserver = null; badgeTarget = null; }
    if (event.target.matches?.('[data-tf-reveal]')) finishReveal(event.target);
    event.target.querySelectorAll('[data-tf-reveal]').forEach(finishReveal);
    event.target.querySelectorAll('.tf-informational-faq details').forEach(details => faqControllers.get(details)?.destroy());
    stories.forEach((state, node) => { if (event.target === node || event.target.contains(node)) state.destroy(); });
  });
  reduced.addEventListener('change', () => {
    if (reduced.matches) {
      Array.from(pending).forEach(finishReveal);
      Array.from(active).forEach(state => state.settle());
      document.documentElement.classList.remove('tf-presentation-ready');
    } else init(document);
  });
  window.addEventListener('pageshow', event => { if (event.persisted) { Array.from(active).forEach(state => state.settle()); stories.forEach(state => state.resume()); init(document); } });
  window.addEventListener('pagehide', () => { Array.from(active).forEach(state => state.settle()); stories.forEach(state => state.suspend()); });
})();
